<template>
  <div class="user-list">
    <div class="user" v-for="user in users" :key="user.id" layout layout-align="space-between">
      <div layout layout-align="start-center">
        <icon class="mr-x1" :name="user.name" :picture-url="user.pictureUrl"></icon>
        <div class="user-detail-container">
          <div class="mr-x2">{{ user.name }}</div>
          <div class="subtext">{{ user.email }}</div>
        </div>
      </div>
      <div v-if="user.id !== meId && !isGuest" layout layout-align="start-center">
        <el-checkbox
          class="mr-x1"
          border
          :checked="user.isAdmin === 1 ? true : false"
          true-label="1"
          false-label="0"
          @change="changeAdminFlg(user, $event)"
          >グループ管理者</el-checkbox
        >
        <el-button type="default" @click="deleteUser(user)" :id="'user-delete-' + user.id"
          ><i class="mdi mdi-delete-forever"></i>削除</el-button
        >
      </div>
      <div v-if="isGuest" layout layout-align="start-center">
        <el-button type="default" @click="deleteGuestUser(user)" :id="'user-delete-' + user.id"
          ><i class="mdi mdi-delete-forever"></i>削除</el-button
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user {
  padding: 12px 0;
  border-top: 1px solid #ebeef5;
}

.user-detail-container {
  display: flex;
  flex-direction: column;
}

.subtext {
  font-size: 12px;
  color: #888;
}
</style>
<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Icon from '@/client/components/_atoms/basic_icon'
import api from '@/client/core/api'
import { TrackingService } from '@/client/services'
import { useSettingGroupStore } from '@/client/store/useSettingGroupStore'
import { useSystemStore } from '@/client/store/useSystemStore'
import { TGuestUser, TUser } from '@/typings/api/group_users.type'

export default defineComponent({
  props: {
    users: Array,
    isGuest: {
      type: Boolean,
      required: false
    }
  },
  components: {
    Icon
  },
  data() {
    return {
      isSubmit: false
    }
  },
  computed: {
    ...mapState(useSystemStore, {
      meId: state => state.user.id,
      currentGroupId: state => state.currentGroup.id,
      groups: state => state.groups
    })
  },
  methods: {
    ...mapActions(useSettingGroupStore, ['fetchUsers', 'fetchGuestUsers']),
    /**
     * 対象グループのユーザーの管理者権限を変更
     * @param {TUser} user ユーザー情報
     * @param {string} isAdmin 管理者権限フラグ
     */
    async changeAdminFlg(user: TUser, isAdmin: string) {
      TrackingService.sendEvent('check:グループ設定|ユーザー|グループ管理者')

      if (this.isSubmit) {
        return
      }

      this.isSubmit = true
      // 対象グループのユーザーの管理者権限を変更
      const result = await api.post('/group_users', {
        groupId: this.currentGroupId,
        userId: user.id,
        isAdmin: Number(isAdmin)
      })
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のユーザーはすでに削除されています。',
          customClass: 'danger',
          duration: 5000
        })
        const { shouldLeaveGroupSetting } = await this.fetchUsers()

        if (shouldLeaveGroupSetting) {
          this.$router.push('/campaigns')
        }

        return
      }

      // エラーがある場合はエラーメッセージを表示
      if (!result.data) {
        this.$notify({
          title: 'グループ管理者権限の保存に失敗しました',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'グループ管理者権限を保存しました。',
        customClass: 'success',
        duration: 5000
      })

      // ユーザー一覧を取得
      const { shouldLeaveGroupSetting } = await this.fetchUsers(this.currentGroupId)

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }
    },
    /**
     * 対象グループのユーザーを削除
     * @param {TUser} user ユーザー情報
     */
    async deleteUser(user: TUser) {
      TrackingService.sendEvent('click:グループ設定|ユーザー|削除')

      if (this.isSubmit) {
        return
      }

      const confirm = window.confirm(user.name + 'をグループから削除してよろしいですか？')
      if (!confirm) {
        window.document.getElementById('user-delete-' + user.id).blur()
        return
      }

      this.isSubmit = true
      // 対象グループのユーザーを削除
      const result = await api.delete('/group_users/' + user.id, {
        params: {
          groupId: this.currentGroupId
        }
      })
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のユーザーはすでに削除されています。',
          customClass: 'danger',
          duration: 5000
        })
        const { shouldLeaveGroupSetting } = await this.fetchUsers()

        if (shouldLeaveGroupSetting) {
          this.$router.push('/campaigns')
        }

        return
      }
      // エラーがある場合はエラーメッセージを表示
      if (!result.data) {
        this.$notify({
          title: '削除に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: '削除に成功しました。',
        customClass: 'success',
        duration: 5000
      })

      // ユーザー一覧を取得
      const { shouldLeaveGroupSetting } = await this.fetchUsers(this.currentGroupId)

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }
    },

    /**
     * 対象グループのユーザーを削除
     * @param {TGuestUser} user ユーザー情報
     */
    async deleteGuestUser(user: TGuestUser) {
      TrackingService.sendEvent('click:グループ設定|ユーザー|削除')

      if (this.isSubmit) {
        return
      }

      const confirm = window.confirm(user.name + 'をグループから削除してよろしいですか？')
      if (!confirm) {
        window.document.getElementById('user-delete-' + user.id).blur()
        return
      }

      this.isSubmit = true
      // 対象グループのユーザーを削除
      const result = await api.delete('/group_guest_users/' + user.id, {
        params: {
          groupId: this.currentGroupId
        }
      })
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のユーザーはすでに削除されています。',
          customClass: 'danger',
          duration: 5000
        })
        const { shouldLeaveGroupSetting } = await this.fetchUsers()

        if (shouldLeaveGroupSetting) {
          this.$router.push('/campaigns')
        }

        return
      }
      // エラーがある場合はエラーメッセージを表示
      if (!result.data) {
        this.$notify({
          title: '削除に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: '削除に成功しました。',
        customClass: 'success',
        duration: 5000
      })

      // ゲストユーザー一覧を取得
      const { shouldLeaveGroupSetting } = await this.fetchGuestUsers(this.currentGroupId)

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }
    }
  }
})
</script>
